import { Fragment, useEffect, useRef, useState } from "react";
import {
  StyledBottomBar,
  StyledCell,
  StyledCellContainer,
  StyledCellContentCollaborators,
  StyledCellContentDownload,
  StyledCellContentGloabal,
  StyledCellContentInternalGrades,
  StyledCircle,
  StyledCircleGroupContainer,
  StyledIconContainer,
  StyledMatrixButton,
  StyledQuadrantAxisLabels,
  StyledMatrix,
} from "../MatrixGrid/MatrixGrid.styled";
import {
  CollaboratorInfo,
  FontWeightEnum,
  IconSizeEnum,
} from "../../types/AppTypes";
import { BodyTyp, CaptionsTyp } from "../Typography/Typography";
import { IconManager } from "../core/IconManager/IconManager";
import { MatrixItem } from "../../types/api/potentialMatrix.type";
import { theme } from "../../constants/StyledComponents";
import { ReactComponent as CheckIcon } from "../../icons/check.svg";
import { useAppSelector } from "../../hooks/store.hook";
import {
  EMPTY_STRING,
  PERCENTAGE_ACHIEVEMENTS_GOALS_LABEL,
  RELEASE_HERE_LABEL,
  LABEL_TOOLTIP_A_POTENTIAL_MATRIX,
  LABEL_TOOLTIP_B_POTENTIAL_MATRIX,
  LABEL_TOOLTIP_C_POTENTIAL_MATRIX,
  LABEL_TOOLTIP_D_POTENTIAL_MATRIX,
} from "../../constants/Dictionary";
import { Loader } from "../core/Loader/Loader";
import {
  internalGradeColors,
  MatrixSizes,
} from "../../constants/PotentialMatrixConstants";
import { ReactComponent as DetailsIcon } from "../../icons/details_search.svg";
import { ReactComponent as RocketIcon } from "../../icons/rocket.svg";
import { ReactComponent as FlagIcon } from "../../icons/flag.svg";
import { ReactComponent as AddIcon } from "../../icons/add.svg";
import { Chip, ChipSizeEnum } from "../core/Chip/Chip";
import { CollaboratorChip } from "../core/CollaboratorChip/CollaboratorChip";
import { DownloadCollaboratorChip } from "../core/DownloadCollaboratorChip/DownloadCollaboratorChip";
import { Divider } from "../core/Divider/Divider";
import { NumberChip } from "../core/NumberChip/NumberChip";
import { useElementWidth } from "../../hooks/useElementWidth.hook";
import {
  DragDropContext,
  Draggable,
  DragUpdate,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { ScreenEnum } from "../../types/api/collaborators.type";
import { getIntervalsByFive } from "../../utils/number.utils";
import { ModalSelectRadio } from "../ModalSelectRadio/ModalSelectRadio";
import { Tooltip } from "../../components/core/Tooltip/Tooltip";
import { MatrixCalibrationProps } from "../MatrixGrid/MatrixGrid";
import { MatrixViewMode } from "../../screens/PotentialMatrix/PotentialMatrix";
import { InternalGradeChip } from "../core/InternalGradeChip/InternalGradeChip";

enum GroupEnum {
  LOW = "low",
  MID = "mid",
  HIGH = "high",
}

const CircleGroupMap: Record<GroupEnum, number> = {
  [GroupEnum.LOW]: 40,
  [GroupEnum.MID]: 70,
  [GroupEnum.HIGH]: 100,
};

type MatrixProps = {
  data: MatrixItem[];
  size: MatrixSizes;
  yearSelected: number;
  loading?: boolean;
  viewMode?: MatrixViewMode;
  onClickResume?: (collaborator: CollaboratorInfo) => void;
  selectedItem?: MatrixItem;
  onClickItem?: (item: MatrixItem) => void;
  matrixCalibration?: MatrixCalibrationProps;
  showDetailsChip?: boolean;
};

export const Matrix = ({
  data,
  selectedItem,
  size,
  yearSelected,
  loading = false,
  viewMode,
  onClickItem,
  onClickResume,
  matrixCalibration,
  showDetailsChip = true,
}: MatrixProps) => {
  const { config } = useAppSelector((state) => state.config);
  const divRef = useRef<HTMLDivElement>(null);
  const matrixWidth = useElementWidth(divRef);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    isDrag: boolean;
  }>({ isOpen: false, isDrag: false });

  const potentialMatrixConfig = config.find(
    (item) => item.year === yearSelected,
  )?.config?.finalInterviewDimensions;

  if (!potentialMatrixConfig) {
    return null;
  }

  const rowValues = [
    {
      label: "A",
      color: "#3A219E",
      tooltipMessage: LABEL_TOOLTIP_A_POTENTIAL_MATRIX,
    },
    {
      label: "B",
      color: "#7152EC",
      tooltipMessage: LABEL_TOOLTIP_B_POTENTIAL_MATRIX,
    },
    {
      label: "C",
      color: "#A794F3",
      tooltipMessage: LABEL_TOOLTIP_C_POTENTIAL_MATRIX,
    },
    {
      label: "D",
      color: "#D0C5F9",
      tooltipMessage: LABEL_TOOLTIP_D_POTENTIAL_MATRIX,
    },
  ];

  const calcMatrix = () => {
    const matrix = [] as [string, number, string, number[]][][];
    rowValues.forEach((rv) => {
      const row = [] as [string, number, string, number[]][];
      potentialMatrixConfig.forEach((c) => {
        const cell: [string, number, string, number[]] = [
          rv.label,
          c.id,
          rv.color,
          c.range,
        ];
        row.push(cell);
      });
      matrix.push(row);
    });
    return matrix;
  };

  const matrix = calcMatrix();

  const getGroupForValue = (
    values: number[] = [0, 100],
    value: number,
  ): GroupEnum => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    if (range === 0) {
      return GroupEnum.MID;
    }

    const step = range / 3;

    if (value < min + step) {
      return GroupEnum.LOW;
    } else if (value < min + 2 * step) {
      return GroupEnum.MID;
    } else {
      return GroupEnum.HIGH;
    }
  };

  const renderGlobalCellContent = (
    matrixItem: MatrixItem,
    countList: number[],
  ) => {
    const group = getGroupForValue(countList, matrixItem.count);

    return (
      <StyledCellContentGloabal>
        <StyledCircleGroupContainer $size={CircleGroupMap[GroupEnum.HIGH]}>
          <StyledCircle
            $size={CircleGroupMap[GroupEnum.HIGH]}
            $isVisible={group === GroupEnum.HIGH}
          />
          <StyledCircle
            $size={CircleGroupMap[GroupEnum.MID]}
            $isVisible={group === GroupEnum.MID || group === GroupEnum.HIGH}
          />
          <StyledCircle $size={CircleGroupMap[GroupEnum.LOW]} $isVisible={true}>
            <BodyTyp variant="1" fontWeight={FontWeightEnum.SEMIBOLD}>
              {matrixItem.count}
            </BodyTyp>
          </StyledCircle>
        </StyledCircleGroupContainer>
      </StyledCellContentGloabal>
    );
  };

  const renderInternalGradeCellContent = (
    matrixItem: MatrixItem,
    matrixWidth: number,
  ) => {
    const textMaxWidth = matrixWidth / 8 - 60;
    const INTERNAL_GRADE_SLOTS = 7;

    return (
      <StyledCellContentInternalGrades>
        {Array.from({ length: INTERNAL_GRADE_SLOTS }, (_, i) => {
          const item = matrixItem.internalGrades[i] ?? null;
          return (
            <div
              key={item?.name ?? `ig-placeholder-${i}`}
              className={`internal-grade-chip ${item == null || item.count === 0 ? "invisible" : ""}`}
            >
              {item != null ? (
                <InternalGradeChip
                  value={item.name}
                  number={item.count}
                  color={internalGradeColors[item.name]}
                  textMaxWidth={textMaxWidth}
                />
              ) : (
                <div style={{ height: 24 }} />
              )}
            </div>
          );
        })}
        <Chip
          option={{
            value: EMPTY_STRING,
            label: (
              <StyledMatrixButton>
                <IconManager
                  size={IconSizeEnum.SMALL}
                  color={theme.colors.white}
                >
                  <DetailsIcon />
                </IconManager>
                <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
                  {matrixItem.count}
                </BodyTyp>
              </StyledMatrixButton>
            ),
          }}
          size={ChipSizeEnum.REGULAR}
          fullWidth
          {...(matrixItem &&
            onClickItem && { onSelectChip: () => onClickItem(matrixItem) })}
          selectedChip
        />
      </StyledCellContentInternalGrades>
    );
  };

  const renderCollaboratorsCellContent = (
    matrixItem: MatrixItem,
    showDetails: boolean,
  ) => {
    const COLLABORATOR_SLOTS = 9;
    const COLLABORATOR_INTERNAL_GRADE_SLOTS = 6;
    const numberOfCollaboratorsToShowed = matrixItem.collaborators.length;
    const paddedCollaborators = Array.from(
      { length: COLLABORATOR_SLOTS },
      (_, i) => matrixItem.collaborators[i] ?? null,
    );

    return (
      <StyledCellContentCollaborators>
        <div className="cell-content-collaborators-header">
          <div className="cell-content-collaborators-container">
            {paddedCollaborators.map((item, index) => (
              <div
                key={item?.id ?? `placeholder-${index}`}
                className={`collaborator-chip-slot ${item == null ? "invisible" : ""}`}
              >
                {item != null ? (
                  <CollaboratorChip
                    collaborator={item}
                    {...(item.internalGrade && {
                      borderColor: internalGradeColors[item.internalGrade],
                    })}
                    onClick={onClickResume}
                    size={ChipSizeEnum.SMALL}
                  />
                ) : (
                  <div style={{ height: 18 }} />
                )}
              </div>
            ))}
            {showDetails && (
              <Chip
                option={{
                  value: EMPTY_STRING,
                  label: (
                    <StyledMatrixButton>
                      <IconManager
                        size={IconSizeEnum.SMALL}
                        color={theme.colors.white}
                      >
                        <DetailsIcon />
                      </IconManager>
                      {matrixItem.count - numberOfCollaboratorsToShowed > 0 && (
                        <BodyTyp
                          fontWeight={FontWeightEnum.SEMIBOLD}
                          variant="3"
                        >
                          +{matrixItem.count - numberOfCollaboratorsToShowed}
                        </BodyTyp>
                      )}
                    </StyledMatrixButton>
                  ),
                }}
                size={ChipSizeEnum.SMALL}
                fullWidth
                {...(matrixItem &&
                  onClickItem && {
                    onSelectChip: () => onClickItem(matrixItem),
                  })}
                selectedChip
              />
            )}
          </div>
        </div>
        <Divider margin={6} vertical />
        <div className="number-chip-box">
          {Array.from({ length: COLLABORATOR_INTERNAL_GRADE_SLOTS }, (_, i) => {
            const internalGrade = matrixItem.internalGrades[i] ?? null;
            return (
              <div
                key={internalGrade?.name ?? `ig-placeholder-${i}`}
                className={`number-chip-container ${internalGrade == null || internalGrade.count === 0 ? "invisible" : ""}`}
              >
                {internalGrade != null ? (
                  <NumberChip
                    id={internalGrade.name}
                    color={internalGradeColors[internalGrade.name]}
                    number={internalGrade.count ?? 0}
                  />
                ) : (
                  <div style={{ height: 18 }} />
                )}
              </div>
            );
          })}
        </div>
      </StyledCellContentCollaborators>
    );
  };

  const renderDownloadCellContent = (matrixItem?: MatrixItem) => {
    return (
      <StyledCellContentDownload>
        {matrixItem && (
          <div className="cell-content-collaborators-header">
            <div className="cell-content-collaborators-container">
              {matrixItem.collaborators.map((item) => (
                <DownloadCollaboratorChip
                  key={item.id}
                  collaborator={item}
                  {...(item.internalGrade && {
                    borderColor: internalGradeColors[item.internalGrade],
                  })}
                />
              ))}
            </div>
          </div>
        )}
      </StyledCellContentDownload>
    );
  };

  const renderCellContent = (
    item: MatrixItem,
    row: string,
    col: number,
    matrixWidth: number,
    count: number,
  ) => {
    if (!isBig) {
      if (
        selectedItem &&
        selectedItem.screen === row &&
        selectedItem.dimensionId === col
      ) {
        return (
          <div className="count-container">
            <IconManager size={IconSizeEnum.SMALL} color={theme.colors.black}>
              <CheckIcon />
            </IconManager>
          </div>
        );
      }
      return null;
    }

    if (viewMode === MatrixViewMode.GLOBAL) {
      if (count === 0) {
        return null;
      }

      return renderGlobalCellContent(
        item,
        data.map((matrixItem) => matrixItem.count),
      );
    }

    if (viewMode === MatrixViewMode.INTERNAL_GRADE) {
      if (count === 0) {
        return null;
      }

      return renderInternalGradeCellContent(item, matrixWidth);
    }

    if (viewMode === MatrixViewMode.COLLABORATOR) {
      if (count === 0) {
        return null;
      }

      return renderCollaboratorsCellContent(item, showDetailsChip);
    }

    if (viewMode === MatrixViewMode.DOWNLOAD) {
      return renderDownloadCellContent(item);
    }

    return null;
  };

  const renderEmptyCellContent = () => {
    if (viewMode === MatrixViewMode.DOWNLOAD) {
      return renderDownloadCellContent();
    }

    return null;
  };

  const getItemData = (row: string, col: number) => {
    return data.find(
      (d) => d.dimensionId === col && d.screen === row && d.count > 0,
    );
  };

  const getCellIdFromCollaborator = (
    screen: ScreenEnum,
    dimensionId: number,
  ) => {
    const configItem = potentialMatrixConfig.find((c) => c.id === dimensionId);
    if (!configItem?.range?.length) return "";

    return `${screen}_${dimensionId}_${configItem.range[0]}_${configItem.range[configItem.range.length - 1]}`;
  };

  const currentCellId = matrixCalibration
    ? getCellIdFromCollaborator(
        (matrixCalibration.pendingCalibration?.finalScreen as ScreenEnum) ??
          (matrixCalibration.collaborator.calibration
            ?.finalScreen as ScreenEnum),
        matrixCalibration.pendingCalibration?.finalDimensionId ??
          matrixCalibration.collaborator.calibration?.finalDimensionId ??
          0,
      )
    : "";

  const startingCellId = matrixCalibration
    ? getCellIdFromCollaborator(
        matrixCalibration.collaborator.calibration
          ?.proposedScreen as ScreenEnum,
        matrixCalibration.collaborator.calibration?.proposedDimensionId ?? 0,
      )
    : "";

  const [hoverCellId, setHoverCellId] = useState<string | null>(null);
  const [tmpNewCellId, setTmpNewCellId] = useState<string | null>(null);

  const onChangePercentage = (cellId: string, newPercentage: number) => {
    if (matrixCalibration) {
      const [screen, dimensionId] = cellId.split("_");
      matrixCalibration.onChange((prev) => ({
        ...prev,
        finalDimensionId: Number.parseInt(dimensionId, 10),
        finalScreen: screen as ScreenEnum,
        finalDimensionPercentage: newPercentage,
        finalComment: prev?.finalComment ?? null,
      }));
      setTmpNewCellId(cellId);
      setModalState({ isOpen: false, isDrag: false });
    }
  };

  const onDragUpdate = (update: DragUpdate) => {
    const newHoverId = update.destination?.droppableId || null;
    if (newHoverId !== hoverCellId) {
      setHoverCellId(newHoverId);
    }
  };

  const onDragEnd = (result: DropResult) => {
    const { destination } = result;

    if (!matrixCalibration || !destination) {
      return;
    }

    setTmpNewCellId(destination.droppableId);
    setModalState({
      isOpen: true,
      isDrag: true,
    });

    setHoverCellId(null);
  };

  const onSelectCancel = () => {
    setTmpNewCellId(currentCellId || startingCellId);
    setModalState({ isOpen: false, isDrag: false });
  };

  const ReleaseHereLabel = () => {
    return (
      <div className="release-content">
        <IconManager size={IconSizeEnum.SMALL} color={theme.colors.black}>
          <AddIcon />
        </IconManager>
        <BodyTyp fontWeight={FontWeightEnum.MEDIUM}>
          {RELEASE_HERE_LABEL}
        </BodyTyp>
      </div>
    );
  };

  const isBig = size === MatrixSizes.BIG;

  useEffect(() => {
    if (currentCellId) {
      setTmpNewCellId(currentCellId);
    }
  }, [currentCellId]);

  return (
    <StyledMatrix $size={size}>
      <div className="matrix-content">
        <div className="row-title">
          {rowValues.map((l) =>
            isBig ? (
              <Tooltip key={l.label} message={l.tooltipMessage} width={150}>
                <div style={{ cursor: "pointer" }}>
                  <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="1">
                    {l.label.toUpperCase()}
                  </BodyTyp>
                </div>
              </Tooltip>
            ) : (
              <CaptionsTyp key={l.label} fontWeight={FontWeightEnum.SEMIBOLD}>
                {l.label.toUpperCase()}
              </CaptionsTyp>
            ),
          )}
        </div>
        <Loader
          loading={loading}
          size={20}
          className="loader-matrix-container"
          contentClassName="loader-matrix-content"
        >
          <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
            <div className="matrix-grid" ref={divRef}>
              {matrix.map((row) =>
                row.map((cell, index) => {
                  const [row, col, color, range] = cell;
                  const item = getItemData(row as string, col as number);
                  const cellId = `${row}_${col}_${range[0]}_${range[range.length - 1]}`;
                  const isNotGlobal =
                    item &&
                    item.count > 0 &&
                    viewMode !== MatrixViewMode.GLOBAL;
                  const isStartingCell = cellId === startingCellId;

                  return (
                    <StyledCell
                      key={`${cell}${index}`}
                      $bgColor={color as string}
                      $size={size}
                      $showDownloadPreview={
                        viewMode === MatrixViewMode.DOWNLOAD
                      }
                    >
                      <StyledCellContainer
                        {...(item &&
                          viewMode === MatrixViewMode.GLOBAL &&
                          onClickItem && {
                            $clickable: true,
                          })}
                      >
                        <div
                          className={
                            isBig
                              ? `cell-main-box ${isNotGlobal ? "active" : ""}`
                              : "cell-main-box-small"
                          }
                          {...(item &&
                            viewMode === MatrixViewMode.GLOBAL &&
                            onClickItem && {
                              onClick: () => onClickItem(item),
                            })}
                        >
                          {data.map((d) => {
                            const [row, col] = cell;
                            if (d.dimensionId === col && d.screen === row) {
                              if (item) {
                                return (
                                  <Fragment
                                    key={`${d.screen}-${d.dimensionId}`}
                                  >
                                    {renderCellContent(
                                      item,
                                      row as string,
                                      col as number,
                                      matrixWidth,
                                      d.count,
                                    )}
                                  </Fragment>
                                );
                              } else {
                                return (
                                  <Fragment
                                    key={`${d.screen}-${d.dimensionId}`}
                                  >
                                    {renderEmptyCellContent()}
                                  </Fragment>
                                );
                              }
                            }
                            return null;
                          })}
                        </div>
                        {matrixCalibration !== undefined && (
                          <Droppable droppableId={cellId}>
                            {(provided, snapshot) => {
                              return (
                                <StyledBottomBar
                                  $isActive={
                                    cellId === currentCellId ||
                                    (hoverCellId === null &&
                                      cellId === currentCellId) ||
                                    (hoverCellId !== null &&
                                      cellId === hoverCellId) ||
                                    cellId === tmpNewCellId
                                  }
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                >
                                  <StyledIconContainer
                                    $isDark={false}
                                    $isVisible={cellId === startingCellId}
                                  >
                                    <IconManager size={IconSizeEnum.XSMALL}>
                                      <RocketIcon />
                                    </IconManager>
                                  </StyledIconContainer>
                                  <div className="draggable-content">
                                    {tmpNewCellId === cellId && (
                                      <Draggable draggableId={cellId} index={0}>
                                        {(dragProvided) => (
                                          <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            {...dragProvided.dragHandleProps}
                                            className="chip-container"
                                          >
                                            <CollaboratorChip
                                              collaborator={
                                                matrixCalibration.collaborator
                                                  .collaborator
                                              }
                                              backgroundColor={
                                                theme.colors.green_3
                                              }
                                              size={ChipSizeEnum.SMALL}
                                              isDraggable
                                              calibration={{
                                                percentage:
                                                  matrixCalibration
                                                    .pendingCalibration
                                                    ?.finalDimensionPercentage ??
                                                  0,
                                                onClickPercentage: () =>
                                                  setModalState({
                                                    isOpen: true,
                                                    isDrag: false,
                                                  }),
                                              }}
                                            />
                                          </div>
                                        )}
                                      </Draggable>
                                    )}

                                    {cellId === startingCellId && (
                                      <div className="chip-container placeholder-chip">
                                        <CollaboratorChip
                                          collaborator={
                                            matrixCalibration.collaborator
                                              .collaborator
                                          }
                                          backgroundColor={
                                            theme.colors.yellow_3
                                          }
                                          size={ChipSizeEnum.SMALL}
                                          isDraggable={false}
                                          calibration={{
                                            percentage:
                                              matrixCalibration.collaborator
                                                .calibration
                                                .proposedDimensionPercentage,
                                          }}
                                        />
                                      </div>
                                    )}
                                    {((!isStartingCell &&
                                      hoverCellId === cellId) ||
                                      (!isStartingCell &&
                                        currentCellId !== cellId &&
                                        snapshot.isDraggingOver)) && (
                                      <ReleaseHereLabel />
                                    )}

                                    <div className="dnd-placeholder">
                                      {provided.placeholder}
                                    </div>
                                  </div>
                                  <StyledIconContainer
                                    $isDark
                                    $isVisible={
                                      (!isStartingCell &&
                                        hoverCellId === null &&
                                        cellId === tmpNewCellId) ||
                                      (!isStartingCell &&
                                        hoverCellId !== null &&
                                        cellId === hoverCellId)
                                    }
                                  >
                                    <IconManager
                                      color={theme.colors.white}
                                      size={IconSizeEnum.XSMALL}
                                    >
                                      <FlagIcon />
                                    </IconManager>
                                  </StyledIconContainer>
                                </StyledBottomBar>
                              );
                            }}
                          </Droppable>
                        )}
                      </StyledCellContainer>
                    </StyledCell>
                  );
                }),
              )}
            </div>
          </DragDropContext>
        </Loader>
      </div>
      <StyledQuadrantAxisLabels
        $size={size}
        $numCol={potentialMatrixConfig.length}
      >
        {potentialMatrixConfig.map((v) => (
          <div className="axis-cell" key={v.initials}>
            {isBig ? (
              <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="1">
                {v.range.length === 2
                  ? `${v.initials} | ${v.range[0]}-${v.range[1]}%`
                  : `${v.initials} | ${EMPTY_STRING}`}
              </BodyTyp>
            ) : (
              <CaptionsTyp fontWeight={FontWeightEnum.SEMIBOLD}>
                {v.range.length === 2
                  ? `${v.range[0]}-${v.range[1]}% `
                  : `${EMPTY_STRING}`}
              </CaptionsTyp>
            )}
          </div>
        ))}
      </StyledQuadrantAxisLabels>
      {matrixCalibration && tmpNewCellId && modalState.isOpen && (
        <ModalSelectRadio<number>
          label={PERCENTAGE_ACHIEVEMENTS_GOALS_LABEL}
          isOpen={modalState.isOpen}
          defaultSelected={
            matrixCalibration.pendingCalibration?.finalDimensionPercentage ?? 0
          }
          radioSelectionItems={getIntervalsByFive([
            Number(tmpNewCellId.split("_")[2]),
            Number(tmpNewCellId.split("_")[3]),
          ])}
          onConfirm={(newPercentage) => {
            onChangePercentage(tmpNewCellId, newPercentage);
          }}
          onCancel={onSelectCancel}
        />
      )}
    </StyledMatrix>
  );
};
